/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState } from "react";
import {
  Button,
  Flex,
  Heading,
  Text,
  FormLabel,
  Checkbox,
} from "@chakra-ui/react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { TimePicker } from "@progress/kendo-react-dateinputs";
import { FaEdit } from "react-icons/fa";
import { MdDeleteSweep } from "react-icons/md";
import { useLookupCities } from "../../../services/lookups-mutations";
import ReactHookFormMultiSelect from "components/forms/react-hook-multiselect";

export default function LastCallTime({
  slaDefaults,
  lastCallTime,
  onPrevStep,
  onNextStep,
}) {
  const [currentEditing, setCurrentEditing] = useState(null);
  const [cities, setCities] = useState([]);

  const {
    mutate: getCities,
    data: citiesList,
    reset: resetCitiesList,
  } = useLookupCities();

  /** ---------------- FORM ---------------- **/
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      city: [],
      lastCallTimeVaultAccessRequired: { date: null, is24: false },
      lastCallTimeNoVaultAccessRequired: { date: null, is24: false },
      lastCallTime,
    },
  });

  const {
    fields: lastCallFields,
    append,
    update,
    remove,
  } = useFieldArray({
    control,
    name: "lastCallTime",
  });

  const selectedCitiesInGrid = React.useMemo(
    () => lastCallFields.map((f) => f.city),
    [lastCallFields],
  );

  useEffect(() => {
    getCities("UAE");
  }, []);

  useEffect(() => {
    if (citiesList) {
      setCities(
        citiesList.data.map((elm) => ({ id: elm.name, text: elm.name })),
      );
      resetCitiesList();
    }
  }, [citiesList]);

  useEffect(() => {
    if (!slaDefaults) return;

    const cityObj = watch("city")?.[0];
    if (!cityObj) return;

    const match = slaDefaults?.lastCallTimings?.find(
      (item) => item.city === cityObj.id,
    );

    if (!match) return;

    setValue("lastCallTimeVaultAccessRequired", {
      date: parseBackendTime(match.lastCallTimeVaultAccessRequired)?.date,
      is24: false,
    });

    setValue("lastCallTimeNoVaultAccessRequired", {
      date: parseBackendTime(match.lastCallTimeNoVaultAccessRequired)?.date,
      is24: false,
    });
  }, [slaDefaults, watch("city")]);

  /** ------------ HELPERS ------------ **/

  const toBackendTime = (date) => {
    if (!(date instanceof Date) || isNaN(date)) return null;

    return `${String(date.getHours()).padStart(2, "0")}:${String(
      date.getMinutes(),
    ).padStart(2, "0")}:00`;
  };

  const parseBackendTime = (str) => {
    if (!str) return { date: null, is24: false };

    const [h, m, s] = str.split(":").map(Number);
    const d = new Date();
    d.setHours(h, m, s || 0, 0);

    return { date: d, is24: false };
  };

  const isCityDisabled = (city) =>
    currentEditing === null && selectedCitiesInGrid.includes(city);

  /** ---------------- SUBMIT ---------------- **/

  const onSubmit = (values) => {
    const base = {
      // BOOLEAN FLAGS
      isLastCallTime24HoursVaultAccess:
        values.lastCallTimeVaultAccessRequired?.is24 || false,

      isLastCallTime24HoursNoVaultAccess:
        values.lastCallTimeNoVaultAccessRequired?.is24 || false,

      // TIME VALUES ONLY IF NOT 24 HOURS
      lastCallTimeVaultAccessRequired: values.lastCallTimeVaultAccessRequired
        ?.is24
        ? null
        : toBackendTime(values.lastCallTimeVaultAccessRequired?.date),

      lastCallTimeNoVaultAccessRequired: values
        .lastCallTimeNoVaultAccessRequired?.is24
        ? null
        : toBackendTime(values.lastCallTimeNoVaultAccessRequired?.date),
    };

    if (currentEditing !== null) {
      update(currentEditing, {
        ...base,
        city: values.city[0]?.id,
      });
      setCurrentEditing(null);
    } else {
      values.city
        .map((c) => c.id)
        .filter((city) => !selectedCitiesInGrid.includes(city))
        .forEach((city) => append({ ...base, city }));
    }

    const updatedFields = getValues("lastCallTime");

    reset({
      city: [],
      lastCallTimeVaultAccessRequired: {
        date: null,
        is24: false,
      },
      lastCallTimeNoVaultAccessRequired: {
        date: null,
        is24: false,
      },
      lastCallTime: updatedFields,
    });
  };

  const cancelEditing = () => {
    setCurrentEditing(null);
    reset({
      city: [],
      lastCallTimeVaultAccessRequired: {
        date: null,
        is24: false,
      },
      lastCallTimeNoVaultAccessRequired: {
        date: null,
        is24: false,
      },
      lastCallTime: getValues("lastCallTime"),
    });
  };

  /** ---------------- CELLS ---------------- **/

  const editCell = ({ dataItem, dataIndex }) => (
    <td>
      <FaEdit
        size={18}
        color="#2B6CB0"
        style={{ cursor: "pointer" }}
        onClick={() => {
          reset({
            city: dataItem.city
              ? [{ id: dataItem.city, text: dataItem.city }]
              : [],
            lastCallTimeVaultAccessRequired: {
              ...parseBackendTime(dataItem.lastCallTimeVaultAccessRequired),
              is24: dataItem.isLastCallTime24HoursVaultAccess || false,
            },
            lastCallTimeNoVaultAccessRequired: {
              ...parseBackendTime(dataItem.lastCallTimeNoVaultAccessRequired),
              is24: dataItem.isLastCallTime24HoursNoVaultAccess || false,
            },
            lastCallTime: getValues("lastCallTime"),
          });

          setCurrentEditing(dataIndex);
        }}
      />
    </td>
  );

  const deleteCell = ({ dataIndex }) => (
    <td>
      <MdDeleteSweep
        size={20}
        color="red"
        style={{ cursor: "pointer" }}
        onClick={() => remove(dataIndex)}
      />
    </td>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
      <Flex w="100%" justify="space-between">
        <Heading style={{ width: "100%" }}>Last Call Time</Heading>

        <Flex w="100%" justify="flex-end">
          <Button
            type="button"
            onClick={() => onPrevStep(getValues("lastCallTime"))}
            mr={3}
          >
            Back
          </Button>
          <Button
            type="button"
            onClick={() => onNextStep(getValues("lastCallTime"))}
          >
            Next
          </Button>
        </Flex>
      </Flex>

      {/* CITY */}
      <div className="w-full">
        <ReactHookFormMultiSelect
          control={control}
          errors={errors}
          name="city"
          label="City"
          isRequired
          filterable
          id="id"
          text="text"
          data={cities}
          placeholder="Select City"
          rules={{
            validate: (value) =>
              value?.length > 0 || "Please select at least one city",
          }}
          multiSelectProps={{
            className: "w-full",
            itemRender: (li, itemProps) => {
              const disabled = isCityDisabled(itemProps.dataItem.id);

              return React.cloneElement(li, {
                style: {
                  ...li.props.style,
                  opacity: disabled ? 0.5 : 1,
                  pointerEvents: disabled ? "none" : "auto",
                },
              });
            },
          }}
        />
      </div>

      {/* VAULT ACCESS TIME */}
      <div>
        <FormLabel fontSize="sm" mb={1}>
          Vault Access Call Time *
        </FormLabel>

        <Controller
          control={control}
          name="lastCallTimeVaultAccessRequired"
          rules={{
            validate: (v) =>
              v?.is24 === true ||
              v?.date instanceof Date ||
              "Vault access time required",
          }}
          render={({ field }) => (
            <Flex align="center" gap="12px">
              <TimePicker
                value={field.value?.date}
                onChange={(e) => field.onChange({ date: e.value, is24: false })}
                format="HH:mm"
                style={{ width: "140px" }}
                disabled={field.value?.is24}
              />

              <Checkbox
                isChecked={field.value?.is24}
                onChange={(e) =>
                  field.onChange({
                    date: null,
                    is24: e.target.checked,
                  })
                }
              >
                24 Hours
              </Checkbox>
            </Flex>
          )}
        />

        {errors.lastCallTimeVaultAccessRequired ? (
          <Text color="red.500" fontSize="xs">
            {errors.lastCallTimeVaultAccessRequired.message}
          </Text>
        ) : null}
      </div>

      {/* NO VAULT ACCESS TIME */}
      <div>
        <FormLabel fontSize="sm" mb={1}>
          No Vault Access Call Time *
        </FormLabel>

        <Controller
          control={control}
          name="lastCallTimeNoVaultAccessRequired"
          rules={{
            validate: (v) =>
              v?.is24 === true ||
              v?.date instanceof Date ||
              "No vault access time required",
          }}
          render={({ field }) => (
            <Flex align="center" gap="12px">
              <TimePicker
                value={field.value?.date}
                onChange={(e) => field.onChange({ date: e.value, is24: false })}
                format="HH:mm"
                style={{ width: "140px" }}
                disabled={field.value?.is24}
              />

              <Checkbox
                isChecked={field.value?.is24}
                onChange={(e) =>
                  field.onChange({
                    date: null,
                    is24: e.target.checked,
                  })
                }
              >
                24 Hours
              </Checkbox>
            </Flex>
          )}
        />

        {errors.lastCallTimeNoVaultAccessRequired ? (
          <Text color="red.500" fontSize="xs">
            {errors.lastCallTimeNoVaultAccessRequired.message}
          </Text>
        ) : null}
      </div>

      <Flex justify="flex-start" width="100%" mb="24px">
        {currentEditing !== null && (
          <Button variant="outline" onClick={cancelEditing} mr={3}>
            Cancel
          </Button>
        )}

        <Button colorScheme="blue" type="submit">
          {currentEditing !== null
            ? "Update Last Call Time"
            : "Add Last Call Time"}
        </Button>
      </Flex>

      <Grid
        data={getValues("lastCallTime")}
        style={{
          border: "1px solid #E2E8F0",
          borderRadius: "8px",
          padding: "8px",
        }}
      >
        <GridColumn field="city" title="City" />

        <GridColumn
          field="lastCallTimeVaultAccessRequired"
          title="Vault Access Call Time"
          cell={(props) => (
            <td>{props.dataItem.lastCallTimeVaultAccessRequired || "-"}</td>
          )}
        />

        <GridColumn
          field="isLastCallTime24HoursVaultAccess"
          title="Vault Access 24 Hours"
          width={180}
          cell={(props) => (
            <td>
              <Checkbox
                isChecked={
                  props.dataItem.isLastCallTime24HoursVaultAccess || false
                }
                isDisabled
              />
            </td>
          )}
        />

        <GridColumn
          field="lastCallTimeNoVaultAccessRequired"
          title="No Vault Access Call Time"
          cell={(props) => (
            <td>{props.dataItem.lastCallTimeNoVaultAccessRequired || "-"}</td>
          )}
        />

        <GridColumn
          field="isLastCallTime24HoursNoVaultAccess"
          title="No Vault Access 24 Hours"
          width={180}
          cell={(props) => (
            <td>
              <Checkbox
                isChecked={
                  props.dataItem.isLastCallTime24HoursNoVaultAccess || false
                }
                isDisabled
              />
            </td>
          )}
        />

        <GridColumn width={80} title="Edit" cell={editCell} />
        <GridColumn width={80} title="Delete" cell={deleteCell} />
      </Grid>
    </form>
  );
}
